import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import * as ObjectUtils from './objectUtils';
import { DetailViewProps } from '../common/components/AdminDataGrid/AdminDataGridEditPanel';
import { EditorModus } from '../common/components/EditDialog/EditDialog';
import { TEntity } from '../../models/entity';
import makeStyles from '@mui/styles/makeStyles';
import { useEditPanelContext } from '../common/components/AdminDataGrid/EditPanelContext';
import { getISOStringWithOutLocalization, isValidDate } from './dateUtils';
import { KeysArray } from '../../models/Constants/RequiredFields';
import { checkIsEmptyField } from './validateUtils';


export type ErrorObject = { [key: string]: string };

export interface FormProps<T extends TEntity> extends Omit<DetailViewProps<T>, 'mode' | 'status' | 'setStatusEditPanel' | 'pristine' | 'setLoading'> {
    initialData: T;
    notSetInitialData?: boolean;
    mappingInitialChildrenErrors?: string[];
}


export type FormInputEventHandler = { target: FormInputField, untouched?: boolean };
export type MultiFormInputEventHandler = { target: FormInputField[], untouched?: boolean };
export type FormInputField = { name: string; value: any };

export interface FormStateHandler<T extends TEntity> {
    value: T;
    isNew: boolean;
    handleFieldValidation: (fieldName: string, isValid: boolean, errorMessage?: string | undefined) => void
    handleInputChange: (e: FormInputEventHandler) => void;
    handleInputChangeMultiFields: (e: MultiFormInputEventHandler) => void;
}

export function blankedOutId(id: number | string) {
    if ((id as number) < 0 || !id)
        return '';
    return id;
}

export function entityKeyGenerator<T extends { id: number }>(list: T[]) {
    return (
        list.reduce((minId, value) => Math.min(minId, value.id), 0) - 1
    );
}

export function isEmptyValue(value: any) {
    if (value == null) return true;
    if (typeof value === 'object') return ObjectUtils.isEmpty(value);
    if (typeof value === 'string') return value.length === 0;
}

export function testNew(props: { mode: EditorModus }): boolean;
export function testNew(mode: EditorModus): boolean;
export function testNew(mode: EditorModus | { mode: EditorModus }): boolean {
    if (typeof mode === 'object' && 'mode' in mode) {
        mode = mode.mode;
    }
    return mode === EditorModus.Kopi || mode === EditorModus.Opprett || mode === EditorModus.OpprettBarn;
}

export function showRequiredMark(value: any, mode?: EditorModus, error?: string, required?: boolean): boolean {
    return (!!required && mode !== EditorModus.Soek);
}

export function markRequiredLabelRed(value: any, mode?: EditorModus, error?: string): boolean {
    return (checkIsEmptyField(value) && (mode === EditorModus.Opprett || mode === EditorModus.Kopi)) || !!error;
}

export function useForm<T extends TEntity>({ initialData, notSetInitialData, mappingInitialChildrenErrors }: FormProps<T>
): FormStateHandler<T> {
    const [formValue, setFormValue] = useState<T>(initialData ?? ({} as T));
    const { setValue, setPristine, validate, mode, handleFieldValidation } = useEditPanelContext<T>();

    const setInitialValue = useCallback(() => {
        const data = !initialData ? {} as T : initialData;
        setFormValue(data);
        setValue(data);
        setPristine(mode !== EditorModus.Kopi);
    }, [initialData, mode, setPristine, setValue]);

    useEffect(() => {
        const data = mode === EditorModus.Soek || !initialData ? {} as T : initialData;
        if (mode === EditorModus.Kopi || mode === EditorModus.Opprett)
            validate(data, []);
        else
            validate(data, [...Object.keys(data), ...mappingInitialChildrenErrors ?? []] as (keyof T)[]);
    }, [initialData, mappingInitialChildrenErrors, mode, validate]);

    useEffect(() => {
        if (notSetInitialData) return;
        setInitialValue();
    }, [setInitialValue, notSetInitialData]);


    const handleInputChange = useCallback(
        (e: FormInputEventHandler) => {
            const { name, value } = e.target;
            let inpValue = value;
            if (value instanceof Date && isValidDate(value)) {
                let date = value;
                inpValue = getISOStringWithOutLocalization(date);
            }
            if (!e.untouched) setPristine(false);
            let newValue = {} as T;
            setValue(prev => {
                if (!prev)
                    newValue = ObjectUtils.setPropValue(newValue, name, inpValue);
                else newValue = ObjectUtils.setPropValue(prev, name, inpValue);
                validate(newValue, [name as keyof T]);
                return newValue;
            });
            setFormValue(prev => ObjectUtils.setPropValue(prev, name, inpValue));
        },
        [setPristine, setValue, validate]
    );

    const handleInputChangeMultiFields = useCallback(
        (e: MultiFormInputEventHandler) => {
            const fields = e.target;
            let currentValue = {} as T;
            if (!e.untouched) setPristine(false);
            setValue(prev => {
                currentValue = prev ?? ({} as T);
                let names: KeysArray<T> = [];
                fields.forEach(field => {
                    let inpValue = field.value;
                    if (field.value instanceof Date && isValidDate(field.value)) {
                        let date = field.value;
                        inpValue = getISOStringWithOutLocalization(date);
                    }
                    names = [...names, field.name as keyof T];
                    currentValue = ObjectUtils.setPropValue(currentValue, field.name, inpValue);
                });
                validate(currentValue, names);
                return currentValue;
            });
            setFormValue(prev => {
                currentValue = prev ?? ({} as T);
                fields.forEach(field => {
                    let inpValue = field.value;
                    if (dayjs.isDayjs(field.value) && field.value.isValid()) {
                        let date = (field.value as unknown as Date);
                        inpValue = getISOStringWithOutLocalization(date);
                    }
                    currentValue = ObjectUtils.setPropValue(currentValue, field.name, inpValue);
                });
                return currentValue;
            });
        },
        [setPristine, setValue, validate]
    );


    const isNew = useMemo(() => testNew(mode), [mode]);
    return {
        value: formValue,
        isNew,
        handleInputChange,
        handleFieldValidation,
        handleInputChangeMultiFields,
    };
}

export const useFormStyles = makeStyles(theme => ({
    inlineContainer: {
        marginLeft: theme.spacing(-0.5),
        marginRight: theme.spacing(-0.5),
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    inlineField: {
        flexGrow: 1,
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
    },
}));
